import * as React from 'react'
import dynamic from 'next/dynamic'

import {t} from '@lib/helpers'
import {AISolutionDetailLayout} from '@components/ai'

const Meta = dynamic(() => import('@components/Meta'))

export default function Agriculture() {
  return (
    <React.Fragment>
      <Meta
        title={`Agriculture and Livestock | Datawow – ${t('layout.title')}`}
      />

      <section className='AISolutionDetail__introduction'>
        <div className='container'>
          <div className='grid grid--max-left'>
            <h1>
              <small>ตัวอย่าง AI Use Case ทางด้าน</small>
              Agriculture<span>and</span>
              <span>Livestock</span>
            </h1>
            <p>
              หลายปีที่ผ่านมามีหลายเทคโนโลยีที่ถูกพัฒนาขึ้นอย่างรวดเร็ว
              ส่งผลให้การเก็บข้อมูลทางการเกษตรเป็นเรื่องง่ายดาย ตัวอย่างเช่น
              การพัฒนาอย่างก้าวกระโดดของอุปกรณ์ IoT เซนเซอร์ต่าง ๆ
              และการพัฒนาของอากาศยานไร้คนขับหรือ Drone นั่นเอง
              เมื่อสามารถเก็บข้อมูลได้โดยสะดวกและแม่นยำแล้ว การนำ AI
              มาใช้เพื่อวิเคราะห์ข้อมูลเพื่อพัฒนาประสิทธิการทำการเกษตรจึงถูกใช้อย่างแพร่หลาย
              มีการคาดการณ์กันว่าในทศวรรษนี้จะมีการเติบโตของการใช้ AI
              ในด้านการเกษตรทั่วโลกกว่าปีละ 20%
              โดยภูมิภาคที่จะมีการเติบโตเร็วที่สุดนั้นคือเอเชียนั่นเอง
              นำโดยประเทศจีน และอินเดีย
            </p>
          </div>
        </div>
      </section>

      <section className='AISolutionDetail__feature-img'>
        <img
          src='/images/ai/agriculture/feature1.jpg'
          srcSet='/images/ai/agriculture/feature1@2x.jpg 2x'
          alt='Agriculture'
        />
      </section>

      <section className='AISolutionDetail__section AISolutionDetail__section--light'>
        <div className='container'>
          <div className='grid grid--max-left'>
            <div>
              <h2>Yield Prediction</h2>
              <img
                src='/images/ai/agriculture/feature2.jpg'
                srcSet='/images/ai/agriculture/feature2@2x.jpg 2x'
                alt='Yield Prediction'
                loading='lazy'
                className='lg'
              />
            </div>

            <div>
              <p>
                ในปัจจุบันมีงานวิจัยมากมายเพื่อประยุกต์ใช้ AI และ Machine
                Learning ในด้านต่าง ๆ ของการเกษตร เช่น การใช้ AI
                เพื่อคาดการณ์ปริมาณผลผลิตล่วงหน้าของพืชต่าง ๆ หรือการใช้ AI
                ในการวิเคราะห์ปัจจัยต่าง ๆ ที่มีความสำคัญต่อผลผลิตของพืชชนิดนั้น
                ๆ
              </p>
              <p>
                อย่างไรก็ตาม การพัฒนา AI
                เพื่อพยากรณ์ผลผลิตให้สำริดผลนั้นจะต้องมีการตระเตรียมข้อมูลล่วงหน้าอย่างเป็นระบบ
                เช่น ถ้าต้องการทำนายน้ำหนักของอ้อยนั้น
                ข้อมูลที่จะต้องตระเตรียมได้แก่ข้อมูลผลผลิตของอ้อยพันธุ์นั้นในภูมิประเทศนั้นในอดีตเป็นจำนวนหลายปี
                โดยที่ข้อมูลควรมีความหลากหลายทั้งในด้านของสภาพแวดล้อม เช่น
                ปริมาณน้ำฝน สภาพความสมบูรณ์ของดินในแต่ละปี
                และกระบวนการปลูกและดูแล เช่น ปุ๋ยที่ใช้ เป็นต้น
              </p>
              <p>
                ในต่างประเทศมีการเปิดใช้ระบบ Open Data Platform
                ซึ่งเปิดให้เกษตรกรจาก
                <span className='AISolutionDetail__span'>หลาย ๆ</span>{' '}
                ภาคส่วนมาร่วมกันบันทึกข้อมูลการปลูกพืชพันธุ์และปริมาณผลผลิตของตนเอง
                และแบ่งปันข้อมูลนี้กับเกษตรกรทุกคน
                เพื่อให้กระบวนการเก็บข้อมูลนั้นเป็นไปอย่างรวดเร็ว
              </p>
            </div>
          </div>

          <img
            src='/images/ai/agriculture/feature2.jpg'
            srcSet='/images/ai/agriculture/feature2@2x.jpg 2x'
            alt='Yield Prediction'
            loading='lazy'
            className='sm'
          />
        </div>
      </section>

      <section className='AISolutionDetail__card'>
        <div className='container'>
          <h2>Crop Monitoring & Yield Estimation</h2>

          <div className='AISolutionDetail__card__body'>
            <div>
              <p>
                นอกจากการทำนายแล้ว การใช้ AI
                ควบคู่กับภาพถ่ายทางอากาศช่วยให้เกษตรกรสามารถทราบสถานะปัจจุบันของแปลงพืชผักขนาดใหญ่ได้ง่ายดายและรวดเร็ว
                ตัวอย่างหนึ่งคือการใช้ AI
                ในการวิเคราะห์และตรวจจับโรคพืชเพื่อเกษตรกรจะได้รีบกำจัดพืชที่ติดโรคอย่างทันท่วงที
                ก่อนที่โรคนั้นจะแพร่กระจายไปในวงกว้าง
                ตัวอย่างเช่นในการปลูกอ้อยนั้นมีการประยุกต์ใช้ภาพถ่ายทางอากาศจากโดรน
                มาใช้ร่วมกับเทคนิคทาง Computer Vision เพื่อหาอ้อยที่เป็นโรคใบขาว
                (White leaf disease) ได้อย่างแม่นยำ
                นอกจากนี้ยังมีการใช้ภาพถ่ายทางอากาศในการหาว่าส่วนใดของแปลงอ้อยที่มีสุขภาพที่ไม่ดี
                มีการตายไปหรือไม่สมบูรณ์ เพื่อที่เกษตรกรสามารถเข้าไปแก้ปัญหา
                หรือปลูกทดแทนได้อย่างรวดเร็ว และให้ได้ผลผลิตต่อพื้นที่มากที่สุด
              </p>
              <p>
                นอกจากที่กล่าวมาแล้ว AI ยังสามารถวิเคราะห์สิ่งอื่น ๆ ได้อีกมาก
                เช่น ประมาณระยะห่างระหว่างอ้อยแต่ละแถว
                ประมาณน้ำหนักของอ้อยจากภาพถ่ายทางอากาศ เป็นต้น
              </p>
            </div>

            <img
              src='/images/ai/agriculture/feature3.jpg'
              srcSet='/images/ai/agriculture/feature3@2x.jpg 2x'
              loading='lazy'
              alt='Crop Monitoring & Yield Estimation'
            />
          </div>
        </div>
      </section>

      <section className='AISolutionDetail__section AISolutionDetail__section--dark'>
        <div className='container'>
          <h2>Livestock Monitoring</h2>

          <div className='grid grid--max-left'>
            <img
              src='/images/ai/agriculture/feature4.jpg'
              srcSet='/images/ai/agriculture/feature4@2x.jpg 2x'
              alt='Livestock Monitoring'
              loading='lazy'
              className='lg'
            />
            <div>
              <p>
                ในด้านของการปศุสัตว์นั้นก็มีการนำ AI มาใช้เช่นกัน
                ตัวอย่างหนึ่งคือการใช้ AI
                ร่วมกับกล้องวงจรปิดเพื่อตรวจจับสัตว์ที่มีอาการผิดปกติ
                หรือสัตว์ที่ตายในโรงเลี้ยง
                และแจ้งเตือนไปยังเกษตรกรเพื่อให้สามารถแยกสัตว์ที่ป่วยหรือกำจัดสัตว์ที่ตายได้อย่างรวดเร็วก่อนที่โรคจะแพร่กระจายไปยังสัตว์ตัวอื่น
                ๆ และก่อให้เกิดความเสียหายตามมา
                นอกจากนี้ในการปศุสัตว์ที่ยุโรปยังมีการใช้ AI ควบคู่กับอุปกรณ์
                IoT ในการพยากรณ์การติดสัดของแม่วัวได้อย่างแม่นยำ
                เพื่อให้ผู้เลี้ยงสามารถเตรียมความพร้อมของการผสมพันธุ์เทียมได้ล่วงหน้าซึ่งจะช่วยให้การเพาะพันธุ์วัวนั้นเป็นไปอย่างมีประสิทธิภาพ
              </p>
            </div>
          </div>

          <img
            src='/images/ai/agriculture/feature4.jpg'
            srcSet='/images/ai/agriculture/feature4@2x.jpg 2x'
            alt='Livestock Monitoring'
            loading='lazy'
            className='sm'
          />
        </div>
      </section>
    </React.Fragment>
  )
}

Agriculture.getLayout = function getLayout(page: React.ReactElement) {
  return <AISolutionDetailLayout>{page}</AISolutionDetailLayout>
}
